import React, { useState, useRef } from 'react';
import { GPXTrace } from '../../services/gpxTraceService';
import './ToolsPanel.css';

interface ToolsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadTrace: (file: File) => void;
    onCreateTrace: (mode: 'walking' | 'cycling' | 'driving', provider: 'osrm' | 'ign') => void;
    onSelectTrace: (traceId: number) => void;
    onDeselectTrace: (traceId?: number) => void;
    onEditTrace: (trace: GPXTrace) => void;
    onEditTraceGeometry: (trace: GPXTrace) => void;
    onExportTrace: () => void;
    onExportSingleTrace: (trace: GPXTrace) => void;
    onShareTrace: (trace: GPXTrace) => void;
    onDeleteTrace: (traceId: number) => void;
    traces: GPXTrace[];
    selectedTraces: GPXTrace[];
    isLoadingTraces: boolean;
    hasMoreTraces: boolean;
    onLoadMoreTraces: () => void;
    totalTraces: number;
}

const ToolsPanel: React.FC<ToolsPanelProps> = ({
    isOpen,
    onClose,
    onUploadTrace,
    onCreateTrace,
    onSelectTrace,
    onDeselectTrace,
    onEditTrace,
    onEditTraceGeometry,
    onExportTrace,
    onExportSingleTrace,
    onShareTrace,
    onDeleteTrace,
    traces,
    selectedTraces,
    isLoadingTraces,
    hasMoreTraces,
    onLoadMoreTraces,
    totalTraces
}) => {
    const [activeSection, setActiveSection] = useState<'traces' | 'create' | 'tools'>('traces');
    const [routingProvider, setRoutingProvider] = useState<'osrm' | 'ign'>('osrm');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.name.endsWith('.gpx')) {
            onUploadTrace(file);
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDeleteTrace = (traceId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette trace ?')) {
            onDeleteTrace(traceId);
        }
    };

    return (
        <>
            {isOpen && <div className="tools-panel-overlay" onClick={onClose} />}
            <div className={`tools-panel ${isOpen ? 'open' : ''}`}>
                <div className="tools-panel-header">
                    <h3>Mes outils</h3>
                    <button className="close-button" onClick={onClose}>
                        <span className="icon">✕</span>
                    </button>
                </div>

                <div className="tools-panel-tabs">
                    <button
                        className={`tab-button ${activeSection === 'traces' ? 'active' : ''}`}
                        onClick={() => setActiveSection('traces')}
                    >
                        <span className="icon">📍</span>
                        Mes traces
                    </button>
                    <button
                        className={`tab-button ${activeSection === 'create' ? 'active' : ''}`}
                        onClick={() => setActiveSection('create')}
                    >
                        <span className="icon">✏️</span>
                        Créer
                    </button>
                    <button
                        className={`tab-button ${activeSection === 'tools' ? 'active' : ''}`}
                        onClick={() => setActiveSection('tools')}
                    >
                        <span className="icon">🔧</span>
                        Outils
                    </button>
                </div>

                <div className="tools-panel-content">
                    {activeSection === 'traces' && (
                        <div className="section">
                            {/* Affichage des traces sélectionnées */}
                            {selectedTraces.length > 0 && (
                                <div className="selected-traces-info">
                                    <div className="selected-traces-header">
                                        <h4>Traces affichées ({selectedTraces.length})</h4>
                                        <button
                                            className="deselect-button"
                                            onClick={() => onDeselectTrace()}
                                            title="Masquer toutes les traces"
                                        >
                                            <span className="icon">✕</span>
                                        </button>
                                    </div>
                                    <div className="selected-traces-list">
                                        {selectedTraces.map(trace => (
                                            <div key={trace.id} className="selected-trace-item">
                                                <div className="trace-name">{trace.original_name}</div>
                                                <button
                                                    className="deselect-trace-button"
                                                    onClick={() => onDeselectTrace(trace.id)}
                                                    title="Masquer cette trace"
                                                >
                                                    <span className="icon">✕</span>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="section-header">
                                <h4>Mes traces GPS</h4>
                                <button
                                    className="action-button primary"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <span className="icon">📁</span>
                                    Uploader
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".gpx"
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                />
                            </div>

                            <div className="traces-list">
                                {isLoadingTraces ? (
                                    <div className="loading">Chargement des traces...</div>
                                ) : traces.length === 0 ? (
                                    <div className="empty-state">
                                        <span className="icon">📍</span>
                                        <p>Aucune trace trouvée</p>
                                        <p className="subtitle">Uploadez votre première trace GPS</p>
                                    </div>
                                ) : (
                                    <>
                                        {traces.map((trace) => {
                                            const isSelected = selectedTraces.some(t => t.id === trace.id);
                                            return (
                                                <div
                                                    key={trace.id}
                                                    className={`trace-item ${isSelected ? 'active' : ''}`}
                                                    onClick={() => onSelectTrace(trace.id)}
                                                >
                                                    <div className="trace-info">
                                                        <div className="trace-name">
                                                            {isSelected && <span className="selected-indicator">📍 </span>}
                                                            {trace.original_name}
                                                        </div>
                                                        <div className="trace-details">
                                                            <span>ID: {trace.id}</span>
                                                            <span>•</span>
                                                            <span>{formatDateTime(trace.created_at)}</span>
                                                        </div>
                                                        {(trace.distance_km !== null || trace.elevation_gain !== null) && (
                                                            <div className="trace-stats">
                                                                {trace.distance_km !== null && (
                                                                    <span className="trace-stat" title="Distance">📏 {trace.distance_km.toFixed(1)} km</span>
                                                                )}
                                                                {trace.elevation_gain !== null && (
                                                                    <span className="trace-stat trace-stat--up" title="Dénivelé positif">↑ {Math.round(trace.elevation_gain)} m</span>
                                                                )}
                                                                {trace.elevation_loss !== null && (
                                                                    <span className="trace-stat trace-stat--down" title="Dénivelé négatif">↓ {Math.round(trace.elevation_loss)} m</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {trace.description && (
                                                            <div className="trace-description">{trace.description}</div>
                                                        )}
                                                    </div>
                                                    <div className="trace-actions">
                                                        <button
                                                            className="export-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onExportSingleTrace(trace);
                                                            }}
                                                            title="Télécharger la trace GPX"
                                                        >
                                                            <span className="icon">💾</span>
                                                        </button>
                                                        <button
                                                            className="delete-button"
                                                            onClick={(e) => handleDeleteTrace(trace.id, e)}
                                                            title="Supprimer la trace"
                                                        >
                                                            <span className="icon">🗑️</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {hasMoreTraces && (
                                            <button
                                                className="load-more-button"
                                                onClick={onLoadMoreTraces}
                                                disabled={isLoadingTraces}
                                            >
                                                {isLoadingTraces ? 'Chargement...' : 'Charger plus'}
                                            </button>
                                        )}

                                        <div className="traces-count">
                                            {traces.length} trace{traces.length > 1 ? 's' : ''} sur {totalTraces}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {activeSection === 'create' && (
                        <div className="section">
                            <h4>Créer une nouvelle trace</h4>
                            <div className="provider-selector">
                                <span className="provider-label">Routing :</span>
                                <button
                                    className={`provider-button ${routingProvider === 'osrm' ? 'active' : ''}`}
                                    onClick={() => setRoutingProvider('osrm')}
                                    title="OpenStreetMap (OSRM)"
                                >
                                    🌍 OSM
                                </button>
                                <button
                                    className={`provider-button ${routingProvider === 'ign' ? 'active' : ''}`}
                                    onClick={() => setRoutingProvider('ign')}
                                    title="IGN Géoportail"
                                >
                                    🗺️ IGN
                                </button>
                            </div>
                            <div className="create-options">
                                <button
                                    className="create-option"
                                    onClick={() => onCreateTrace('walking', routingProvider)}
                                >
                                    <span className="icon">🚶</span>
                                    <div>
                                        <div className="option-title">À pied</div>
                                        <div className="option-subtitle">Randonnée, marche</div>
                                    </div>
                                </button>
                                <button
                                    className="create-option"
                                    onClick={() => onCreateTrace('cycling', routingProvider)}
                                >
                                    <span className="icon">🚴</span>
                                    <div>
                                        <div className="option-title">Vélo</div>
                                        <div className="option-subtitle">VTT, route</div>
                                    </div>
                                </button>
                                <button
                                    className="create-option"
                                    onClick={() => onCreateTrace('driving', routingProvider)}
                                >
                                    <span className="icon">🚗</span>
                                    <div>
                                        <div className="option-title">Voiture</div>
                                        <div className="option-subtitle">Itinéraire routier</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {activeSection === 'tools' && (
                        <div className="section">
                            <h4>Outils des traces</h4>
                            {selectedTraces.length > 0 ? (
                                <div className="tools-actions">
                                    <div className="tools-info">
                                        <p>{selectedTraces.length} trace(s) sélectionnée(s)</p>
                                    </div>
                                    <button
                                        className="tool-action"
                                        onClick={() => onEditTrace(selectedTraces[0])}
                                        disabled={selectedTraces.length !== 1}
                                        title={selectedTraces.length !== 1 ? 'Sélectionnez une seule trace pour éditer' : ''}
                                    >
                                        <span className="icon">✏️</span>
                                        <div>
                                            <div className="action-title">Éditer</div>
                                            <div className="action-subtitle">Modifier les informations</div>
                                        </div>
                                    </button>
                                    <button
                                        className="tool-action"
                                        onClick={() => onEditTraceGeometry(selectedTraces[0])}
                                        disabled={selectedTraces.length !== 1}
                                        title={selectedTraces.length !== 1 ? 'Sélectionnez une seule trace pour modifier le tracé' : ''}
                                    >
                                        <span className="icon">🗓️</span>
                                        <div>
                                            <div className="action-title">Modifier le tracé</div>
                                            <div className="action-subtitle">Éditer les points de passage</div>
                                        </div>
                                    </button>
                                    <button
                                        className="tool-action"
                                        onClick={onExportTrace}
                                    >
                                        <span className="icon">💾</span>
                                        <div>
                                            <div className="action-title">Exporter</div>
                                            <div className="action-subtitle">Télécharger GPX</div>
                                        </div>
                                    </button>
                                    <button
                                        className="tool-action"
                                        onClick={() => onShareTrace(selectedTraces[0])}
                                        disabled={selectedTraces.length !== 1}
                                        title={selectedTraces.length !== 1 ? 'Sélectionnez une seule trace pour partager' : ''}
                                    >
                                        <span className="icon">🔗</span>
                                        <div>
                                            <div className="action-title">Partager</div>
                                            <div className="action-subtitle">Lien public</div>
                                        </div>
                                    </button>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <span className="icon">📍</span>
                                    <p>Aucune trace sélectionnée</p>
                                    <p className="subtitle">Sélectionnez une trace pour utiliser les outils</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ToolsPanel;
